<template>
    <div class="product-tile">
        <div v-if="isTicketType && productSubcategory"
            class="tile__ribbon"
            :style="{backgroundColor: productSubcategory.color}"
        >
            <h3>{{ getTranslatedStr(productSubcategory, 'name', productSubcategory.name) }}</h3>
        </div>
        <div id="product-tile-link"
            class="tile"
            @click="goToProductDetailsPage"
        >
            <div class="tile__header"
                :style="`background-image: url(${productImage})`"
            >
                <out-of-stock v-if="isOutOfStock"
                    class="tile__out-of-stock"
                />
                <coming-soon-timer id="product-tile-coming-soon"
                    class="tile__coming-soon-timer"
                    :endDate="redeemableAt"
                    noPrefix
                />
                <token-participation-amount class="tile__participation-amount"
                    :amount="participationAmount"
                    :partnerId="partnerId"
                    :cardView="true"
                />
            </div>
            <product-card-details id="product-tile-ssu-price"
                :ssuPrice="updateSSU"
                :productTitle="productTitle"
                :reverseAuction="isReverseAuctionType"
                :packageProducts="isPackageProductsType"
            />
        </div>
    </div>
</template>
<script>
// 3rd party libs
import {
    mapGetters,
    mapActions,
} from 'vuex';

import localStoreTypes from '@webapp/store/types.js';

// Components
import tokenParticipationAmount from '@webapp/modules/rewards/components/fanshop/token-participation-amount.vue';
import outOfStock from '@webapp/modules/rewards/components/fanshop/out-of-stock.vue';
import comingSoonTimer from '@webapp/components/timer/coming-soon-timer.vue';
import productCardDetails from '@webapp/modules/rewards/components/fanshop/product-card-details.vue';

// Types
import {
    FanShopProductTypes,
    RedeemTypes,
} from '@shared/modules/rewards/types/constants';

export default {
    name: 'fanshop-product-card',
    components: {
        comingSoonTimer,
        tokenParticipationAmount,
        outOfStock,
        productCardDetails,
    },
    props: {
        productTitle: {
            type: String,
            default: 'Product placeholder',
        },
        productImage: {
            type: String,
            default: '',
        },
        ssuPrice: {
            type: Number,
            default: 0,
        },
        partnerId: {
            type: String,
            default: '',
        },
        participationAmount: {
            type: Number,
            default: 0,
        },
        variantId: {
            type: String,
            default: '',
        },
        redeemableAt: {
            type: String,
            default: '',
        },
        variant: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters(['getTranslatedStr', 'kycTierLevel']),

        ...mapGetters('fanshop', ['getProductSubCategoriesById']),

        updateSSU() {
            return this.ssuPrice * this.variant?.product.quantityStep;
        },

        productSubcategory() {
            return this.getProductSubCategoriesById[this.variant?.product?.categories[0]?.id];
        },

        isReverseAuctionType() {
            return this.variant?.product?.redeemType === RedeemTypes.REVERSE_AUCTION;
        },

        isPackageProductsType() {
            return this.variant?.product?.isPackage;
        },

        isTicketType() {
            return this.variant?.product?.productType.name === FanShopProductTypes.Ticket;
        },

        hasStock() {
            return this.variant?.availableStock > 0;
        },

        isOutOfStock() {
            const comingSoon = this.redeemableAt && new Date(this.redeemableAt).getTime() > Date.now();

            return this.redeemableAt && !comingSoon && !this.hasStock;
        },
    },
    methods: {
        ...mapActions({
            setModal: localStoreTypes.SET_MODAL,
        }),

        goToProductDetailsPage() {
            if (this.kycTierLevel < 2) {
                this.setModal({
                    isVisible: true,
                    componentToLoad: 'components/kyc/kyc-level-up-modal',
                });
            } else {
                this.$router.push({
                    name: 'fanshopProduct',
                    params: {
                        variant: this.variantId,
                    },
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
a {
    text-decoration: none;
}

.product-tile {
    width: 100%;
    max-width: 18rem;
    background-color: $white-color;
    border-radius: 1rem;

    &:hover {
        cursor: pointer;
    }
}

.tile {
    color: $cyprus-color;
    transition: 0.05s ease-in;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: $white-color;
    border-radius: 1rem;

    @include max-width(320px) {
        width: 26.5rem;
    }

    @include max-width(460px) {
        margin: 2rem auto 3rem;
    }

    &__participation-amount {
        position: absolute;
        top: 1.5rem;
        left: 1rem;
    }

    &__header {
        height: 16rem;
        width: 100%;
        position: relative;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        border-radius: 8px 8px 0 0;
    }

    &__ribbon {
        border-radius: 8px 8px 0 0;
        padding: 4px 0 10px;
        text-align: center;
        margin-bottom: -7px;
        color: $white-color;
        width: 100%;

        h3 {
            margin: 0;
        }
    }

    &__coming-soon-timer,
    &__out-of-stock {
        height: 16rem;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: rgba(0, 33, 55, 0.8);
        color: $white-color !important;
    }

    &__coming-soon-timer {
        ::v-deep .coming-soon-timer__container {
            margin-top: 5px;
        }
    }

    &__out-of-stock {
        justify-content: center;
        align-items: center;

        ::v-deep p {
            padding: 0 1rem;
            text-align: center;
        }
    }
}
</style>